/**
 * PNG satelital sobre el mapa, sin GroundOverlay de Google.
 *
 * GroundOverlay, en un mapa vectorial (mapId), agranda el PNG con suavizado.
 * Estas imágenes cubren todos los polígonos (~10 m por píxel). Al hacer zoom
 * cada píxel del archivo ocupa varias pantallas y se ve borroso. Además la
 * misma URL se montaba dos veces (el watcher de la imagen y la capa del mapa).
 *
 * Acá el PNG es un <img> anclado a sus bounds. image-rendering: pixelated
 * deja los píxeles del archivo nítidos, como el zoom de Photopea.
 * La misma URL no se pinta dos veces.
 *
 * Lo usa SimpleMap para todos los tenants.
 */

export interface SharpGroundOverlayHandle {
    setMap(map: google.maps.Map | null): void;
    getBounds(): google.maps.LatLngBounds;
    getUrl(): string;
    setOpacity(opacity: number): void;
}

type OverlayBounds = google.maps.LatLngBounds | { nw: number[]; se: number[] };

let SharpGroundOverlayCtor: any = null;

/**
 * La clase extiende google.maps.OverlayView, que no existe hasta que carga
 * el script de Maps. Se define la primera vez que se muestra una capa.
 */
function getSharpGroundOverlayCtor() {
    if (SharpGroundOverlayCtor) {
        return SharpGroundOverlayCtor;
    }

    SharpGroundOverlayCtor = class SharpGroundOverlay extends google.maps.OverlayView {
        private bounds: google.maps.LatLngBounds;
        private url: string;
        private opacity: number;
        private div: HTMLDivElement | null = null;

        constructor(url: string, bounds: google.maps.LatLngBounds, opacity: number) {
            super();
            this.url = url;
            this.bounds = bounds;
            this.opacity = opacity;
        }

        /** Crea el <img> del PNG y lo pone en la capa del mapa, debajo de los polígonos. */
        onAdd() {
            const div = document.createElement('div');
            div.style.position = 'absolute';
            div.style.overflow = 'hidden';
            div.style.border = '0';
            div.style.opacity = String(this.opacity);

            const img = document.createElement('img');
            img.alt = '';
            img.draggable = false;
            img.src = this.url;
            img.style.cssText = [
                'position:absolute',
                'left:0',
                'top:0',
                'width:100%',
                'height:100%',
                'pointer-events:none',
                'image-rendering:-webkit-optimize-contrast',
                'image-rendering:crisp-edges',
                'image-rendering:pixelated',
            ].join(';');
            div.appendChild(img);

            this.div = div;
            const panes = this.getPanes();
            if (panes) {
                panes.overlayLayer.appendChild(div);
            }
        }

        /**
         * Ubica el PNG en los bounds geográficos, en píxeles enteros.
         * Un solo escalado, del archivo al tamaño del mapa. Sin el paso
         * extra de GroundOverlay, que lo achicaba y después lo volvía a agrandar.
         */
        draw() {
            if (!this.div) return;
            const projection = this.getProjection();
            if (!projection) return;
            const sw = projection.fromLatLngToDivPixel(this.bounds.getSouthWest());
            const ne = projection.fromLatLngToDivPixel(this.bounds.getNorthEast());
            if (!sw || !ne) return;

            const left = Math.round(Math.min(sw.x, ne.x));
            const top = Math.round(Math.min(sw.y, ne.y));
            const width = Math.max(1, Math.round(Math.abs(ne.x - sw.x)));
            const height = Math.max(1, Math.round(Math.abs(sw.y - ne.y)));

            this.div.style.left = left + 'px';
            this.div.style.top = top + 'px';
            this.div.style.width = width + 'px';
            this.div.style.height = height + 'px';
        }

        /** Saca el PNG del mapa. */
        onRemove() {
            if (this.div && this.div.parentNode) {
                this.div.parentNode.removeChild(this.div);
            }
            this.div = null;
        }

        getBounds() {
            return this.bounds;
        }

        getUrl() {
            return this.url;
        }

        /** Cambia la opacidad sin recrear la imagen. */
        setOpacity(opacity: number) {
            this.opacity = opacity;
            if (this.div) {
                this.div.style.opacity = String(opacity);
            }
        }
    };

    return SharpGroundOverlayCtor;
}

function createSharpGroundOverlay(
    url: string,
    bounds: google.maps.LatLngBounds,
    opacity: number
): SharpGroundOverlayHandle {
    const Ctor = getSharpGroundOverlayCtor();
    return new Ctor(url, bounds, opacity);
}

/** Acepta LatLngBounds o el { nw, se } que manda el backend. */
function toLatLngBounds(imageBounds: OverlayBounds | null): google.maps.LatLngBounds | null {
    if (!imageBounds) return null;
    if (typeof (imageBounds as google.maps.LatLngBounds).getNorthEast === 'function') {
        return imageBounds as google.maps.LatLngBounds;
    }
    const nw = (imageBounds as { nw: number[] }).nw;
    const se = (imageBounds as { se: number[] }).se;
    if (!nw || !se) return null;
    const sw = new google.maps.LatLng(se[1], nw[0]);
    const ne = new google.maps.LatLng(nw[1], se[0]);
    return new google.maps.LatLngBounds(sw, ne);
}

/** Capas PNG visibles en un mapa. La misma URL queda una sola vez. */
export class SatellitalGroundOverlays {
    private overlays: Array<{ url: string; overlay: SharpGroundOverlayHandle }> = [];

    /**
     * Muestra el PNG. Si esa URL ya está, no crea otra copia.
     * Devuelve los bounds por si el mapa tiene que centrarse ahí.
     */
    show(
        map: google.maps.Map,
        imageUrl: string,
        imageBounds: OverlayBounds | null
    ): google.maps.LatLngBounds | null {
        if (!map || !imageUrl || !imageBounds) return null;
        const bounds = toLatLngBounds(imageBounds);
        if (!bounds) return null;

        const idx = this.overlays.findIndex(item => item.url === imageUrl);
        if (idx !== -1) {
            this.overlays[idx].overlay.setMap(map);
            return bounds;
        }

        const overlay = createSharpGroundOverlay(imageUrl, bounds, 1);
        overlay.setMap(map);
        this.overlays.push({ url: imageUrl, overlay });
        return bounds;
    }

    /** Oculta la capa de esa URL. */
    hide(url: string) {
        const idx = this.overlays.findIndex(item => item.url === url);
        if (idx === -1) return;
        this.overlays[idx].overlay.setMap(null);
        this.overlays.splice(idx, 1);
    }

    /** Oculta todas las capas PNG. */
    hideAll() {
        this.overlays.forEach(item => item.overlay.setMap(null));
        this.overlays = [];
    }

    /** Cambia la opacidad de la capa de esa URL. */
    setOpacity(url: string, opacity: number) {
        const idx = this.overlays.findIndex(item => item.url === url);
        if (idx === -1) return;
        this.overlays[idx].overlay.setOpacity(opacity);
    }
}
